import { styled } from "styled-components";

import type { useTranslate } from "@similarweb/i18n-provider";
import type {
    ProWebsiteModelsConversionRatesDtoRateLeaderEntry,
    ProWebsiteModelsConversionRatesDtoVolumeLeaderEntry,
} from "@similarweb/pro-api-client";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { TextCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { ChangeValue, ItemIcon, Type } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { CONVERSION_RATE_TRANSLATION_KEYS, conversionRateFormatters } from "../../consts";

type Translate = ReturnType<typeof useTranslate>["translate"];

type VolumeRow = ProWebsiteModelsConversionRatesDtoVolumeLeaderEntry;
type RateRow = ProWebsiteModelsConversionRatesDtoRateLeaderEntry;

const StyledItemIcon = styled(ItemIcon)`
    width: 20px;
    height: 20px;
`;

const renderRank = (rank: number) => (
    <Flex justifyContent="flex-start">
        <TextCell>{`#${rank}`}</TextCell>
    </Flex>
);

const renderDomain = (domain: string, automationPrefix: string) => (
    <Flex
        alignItems="center"
        gap="8px"
    >
        <StyledItemIcon
            iconType={Type.Website}
            iconSrc={getFaviconUrl(domain)}
        />
        <Text
            fontVariant="input2"
            color={colorsPalettes.carbon[400]}
            data-automation={`${automationPrefix}-domain`}
        >
            {domain}
        </Text>
    </Flex>
);

const renderChange = (change: number | null | undefined, formatter: (value: number) => string) => {
    if (change == null) {
        return (
            <Flex justifyContent="flex-end">
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[200]}
                >
                    —
                </Text>
            </Flex>
        );
    }

    return (
        <Flex justifyContent="flex-end">
            <ChangeValue
                value={formatter(change)}
                isDecrease={change < 0}
                unsigned={change === 0}
            />
        </Flex>
    );
};

export const getVolumeLeadersColumnConfig = (
    translate: Translate,
    automationPrefix: string,
): ColumnsType<VolumeRow> => [
    {
        key: "rank",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColRank),
        render: (_, row) => renderRank(row.rank),
        width: 60,
    },
    {
        key: "domain",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColDomain),
        render: (_, row) => renderDomain(row.domain, `${automationPrefix}-row-${row.rank}`),
    },
    {
        key: "convertedVisits",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColConvertedVisits),
        align: "right",
        render: (_, row) => (
            <Flex justifyContent="flex-end">
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[400]}
                    data-automation={`${automationPrefix}-row-${row.rank}-value`}
                >
                    {conversionRateFormatters.convertedVisits(row.convertedVisits)}
                </Text>
            </Flex>
        ),
        width: 140,
    },
    {
        key: "change",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColChange),
        align: "right",
        render: (_, row) => renderChange(row.convertedVisitsChange, conversionRateFormatters.visitsChange),
        width: 100,
    },
];

export const getRateLeadersColumnConfig = (translate: Translate, automationPrefix: string): ColumnsType<RateRow> => [
    {
        key: "rank",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColRank),
        render: (_, row) => renderRank(row.rank),
        width: 60,
    },
    {
        key: "domain",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColDomain),
        render: (_, row) => renderDomain(row.domain, `${automationPrefix}-row-${row.rank}`),
    },
    {
        key: "conversionRate",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColConversionRate),
        align: "right",
        render: (_, row) => (
            <Flex justifyContent="flex-end">
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[400]}
                    data-automation={`${automationPrefix}-row-${row.rank}-value`}
                >
                    {conversionRateFormatters.avgConversionRate(row.conversionRate)}
                </Text>
            </Flex>
        ),
        width: 140,
    },
    {
        key: "change",
        title: translate(CONVERSION_RATE_TRANSLATION_KEYS.leadersColChange),
        align: "right",
        render: (_, row) => renderChange(row.conversionRateChange, conversionRateFormatters.rateChange),
        width: 100,
    },
];

export type { VolumeRow, RateRow };
